import { Link } from "react-router-dom";
import { ArrowRight, Clock, Bookmark } from "lucide-react";
import CTASection from "../components/CTASection";

const POSTS = [
  {
    cat: "Growth", title: "How Lush Boutique 3x'd revenue in 90 days",
    excerpt: "Adunni shares the operational changes that took her boutique from struggling to thriving — without hiring a single new person.",
    img: "https://images.pexels.com/photos/27141314/pexels-photo-27141314.jpeg?auto=compress&cs=tinysrgb&h=650&w=940",
    date: "Mar 12, 2026", read: "8 min", accent: "bg-[#FFF9E6]",
  },
  {
    cat: "Operations", title: "The 5 inventory mistakes killing your margins",
    excerpt: "If you're losing 8% of revenue to stock-outs, dead stock, or shrinkage — you're not alone. Here's how to fix it.",
    img: "https://images.unsplash.com/photo-1763718528755-4bca23f82ac3?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    date: "Mar 8, 2026", read: "6 min", accent: "bg-[#FFE4E8]",
  },
  {
    cat: "Tools", title: "Why we built Brivo's analytics from scratch",
    excerpt: "Off-the-shelf charting libraries weren't fast enough. Here's the engineering story behind a 100ms dashboard.",
    img: "https://images.unsplash.com/photo-1766430342829-9896223959db?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    date: "Mar 4, 2026", read: "12 min", accent: "bg-[#E8F5E9]",
  },
  {
    cat: "Money", title: "How to set up multi-currency without losing money",
    excerpt: "FX rates, settlement timing, payment processor fees — a full guide for businesses going international.",
    img: "https://images.pexels.com/photos/9973170/pexels-photo-9973170.jpeg?auto=compress&cs=tinysrgb&h=650&w=900",
    date: "Feb 28, 2026", read: "10 min", accent: "bg-[#FFF9E6]",
  },
  {
    cat: "Stories", title: "Marcus's path from market stall to 4-store chain",
    excerpt: "What changed at each phase of growth — and the systems Marcus credits for never losing his sanity.",
    img: "https://images.unsplash.com/photo-1765648636178-60e73bcc865e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    date: "Feb 22, 2026", read: "7 min", accent: "bg-[#FFE4E8]",
  },
  {
    cat: "Tools", title: "Brivo + WooCommerce: the integration setup",
    excerpt: "Step-by-step guide to syncing your existing WooCommerce catalog into Brivo without a single duplicate.",
    img: "https://images.pexels.com/photos/4109063/pexels-photo-4109063.jpeg?auto=compress&cs=tinysrgb&h=650&w=900",
    date: "Feb 17, 2026", read: "5 min", accent: "bg-[#E8F5E9]",
  },
];

export default function Blog() {
  const featured = POSTS[0];
  const rest = POSTS.slice(1);

  return (
    <div data-testid="blog-page">
      <section className="hero-gradient relative overflow-hidden grain">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-24 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full glass-strong border border-[#1B4332]/10 text-black text-xs font-semibold mb-6">Brivo Blog</span>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-black tracking-tight leading-[1.05]">
            Stories. Lessons.<br />
            <span className="italic text-black/60 font-medium">Practical wisdom.</span>
          </h1>
          <p className="mt-7 text-lg text-black/70 max-w-2xl mx-auto leading-relaxed">
            Real tactics from real business owners — plus product updates, engineering deep-dives, and the occasional rant.
          </p>
        </div>
      </section>

      {/* Featured */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <article className="grid lg:grid-cols-2 gap-10 items-center bg-[#FFFCF2] rounded-3xl overflow-hidden border border-[#1B4332]/8">
            <img src={featured.img} alt={featured.title} className="w-full aspect-[4/3] object-cover" />
            <div className="p-10 lg:p-12">
              <span className="inline-block px-3 py-1 rounded-full bg-[#FFE4E8] text-black text-xs font-semibold mb-4">{featured.cat}</span>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-black tracking-tight leading-tight">{featured.title}</h2>
              <p className="mt-4 text-black/70 leading-relaxed">{featured.excerpt}</p>
              <div className="mt-6 flex items-center gap-4 text-sm text-black/50">
                <span>{featured.date}</span>
                <span className="flex items-center gap-1"><Clock size={14} /> {featured.read} read</span>
              </div>
              <Link to="#" className="mt-7 inline-flex items-center gap-2 text-black font-semibold hover:gap-3 transition-all">
                Read article <ArrowRight size={18} />
              </Link>
            </div>
          </article>
        </div>
      </section>

      <section className="py-16 bg-[#FFFCF2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-black mb-10">Latest articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <article key={i} className={`${p.accent} rounded-3xl overflow-hidden border border-[#1B4332]/8 card-hover group`}>
                <img src={p.img} alt={p.title} className="w-full aspect-[16/10] object-cover" />
                <div className="p-7">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-black/70">{p.cat}</span>
                    <Bookmark size={14} className="text-black/40" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-black mb-3 leading-tight">{p.title}</h3>
                  <p className="text-sm text-black/70 leading-relaxed mb-5">{p.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-black/50 pt-4 border-t border-[#1B4332]/10">
                    <span>{p.date}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {p.read}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <CTASection accent="mint" title="Get our weekly newsletter" subtitle="Practical tips for growing your business — delivered every Friday." />
    </div>
  );
}
